import React, { useState, useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import Modal from '../../components/Modal';
import Button from '../../components/Button';
import { QUERY as GET_QUIZ, Response, Variables as QuizVars } from '../queries/getQuizQuery';
import {
  UPDATE_QUIZ_RESPONSE_MUTATION,
  UpdateQuizResponseVariables,
  UpdateQuizResponseResponse,
} from '../hooks/useUpdateQuizResponseMutation';
import { UPDATE_QUIZ } from '../mutations/updateQuizReportData';
import { PREPARE_FILE_FOR_ZIP } from '../mutations/prepareFileForZipMutation';
import { PACK_REPORTS_ZIP } from '../mutations/packReportsZipMutation';
import { UPLOAD_REPORTS_ZIP } from '../mutations/uploadReportsZipMutation';
import {
  QUERY as GET_INDIVIDUAL_PDF,
  Variables as IndividualPdfVars,
  Response as IndividualPdfResponse,
} from './SurveyResponse/queries/getIndividualReportPdfQuery';
import {
  QUERY as GET_LEADER_PDF,
  Variables as LeaderPdfVars,
  Response as LeaderPdfResponse,
} from './SurveyResponse/queries/getLeaderReportPdfQuery';

type Step = 'confirm' | 'processing' | 'downloading' | 'packing' | 'uploading' | 'done';

type Props = {
  readonly survey: Response['quiz'];
  readonly onClose: () => void;
  readonly refetch: () => void;
};

function generateBatchId() {
  return 'batch-' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
}

export default function DownloadAllReportsModal({ survey, onClose, refetch }: Props) {
  const client = useApolloClient();
  const [step, setStep] = useState<Step>('confirm');
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState({ current: 0, total: 0, label: '' });
  const [zipUrl, setZipUrl] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  const totalResponses = survey.responses.length;
  const needsAiData = survey.responses.filter((r) => !r.aiReportData).length;
  const needsApproval = survey.responses.filter((r) => !r.isReportApproved).length;
  const hasTeamReport = survey.aiReportData != null;

  const runProcess = useCallback(async () => {
    const collectedFiles: { url: string; filename: string }[] = [];
    const newWarnings: string[] = [];
    const batchId = generateBatchId();

    setStep('processing');
    setError(null);

    // Fetch fresh data to avoid processing already-approved responses
    const freshResult = await client.query<Response, QuizVars>({
      query: GET_QUIZ,
      variables: { id: survey.id },
      fetchPolicy: 'network-only',
      errorPolicy: 'none',
    });
    const freshSurvey = freshResult.data.quiz;
    const freshHasTeamReport = freshSurvey.aiReportData != null;

    const total = freshSurvey.responses.length + (freshHasTeamReport ? 1 : 0);

    // === STEP 1: Process each response (AI data + approve + get PDF URL) ===
    for (let i = 0; i < freshSurvey.responses.length; i++) {
      const response = freshSurvey.responses[i];
      const name = response.respondentName || `Response ${i + 1}`;
      const typeLabel = response.respondentType === 'LEADER' ? 'Leader' : 'Individual';

      try {
        const safeName = name.replace(/[^a-zA-Z0-9-_ ]/g, '').trim();

        // Skip if already has AI data, is approved, and has a PDF file
        if (response.aiReportData && response.isReportApproved && response.reportUrl) {
          setProgress({ current: i, total, label: `${name} - already done, skipping` });
          collectedFiles.push({ url: response.reportUrl, filename: `${safeName} - ${typeLabel} Report.pdf` });
          setProgress({ current: i + 1, total, label: '' });
          continue;
        }

        // Generate AI data if missing, or just approve if needed
        if (!response.aiReportData || !response.isReportApproved) {
          const needsRegeneration = !response.aiReportData;
          setProgress({
            current: i, total,
            label: needsRegeneration
              ? `Generating AI data for ${name}...`
              : `Approving ${name}...`,
          });

          await client.mutate<UpdateQuizResponseResponse, UpdateQuizResponseVariables>({
            mutation: UPDATE_QUIZ_RESPONSE_MUTATION,
            variables: {
              id: response.id,
              input: {
                isReportApproved: true,
                aiReportData: needsRegeneration ? null : (response.aiReportData as any),
              },
            },
            errorPolicy: 'none',
          });
        }

        // Use existing PDF if available, otherwise generate
        if (response.reportUrl) {
          collectedFiles.push({ url: response.reportUrl, filename: `${safeName} - ${typeLabel} Report.pdf` });
        } else {
          setProgress({ current: i, total, label: `Generating PDF for ${name}...` });

          const pdfResult = await client.query<IndividualPdfResponse, IndividualPdfVars>({
            query: GET_INDIVIDUAL_PDF,
            variables: { responseId: response.id },
            fetchPolicy: 'network-only',
            errorPolicy: 'none',
          });

          const url = pdfResult.data?.getIndividualReportPdf?.reportUrl;
          if (url) {
            collectedFiles.push({ url, filename: `${safeName} - ${typeLabel} Report.pdf` });
          } else {
            newWarnings.push(`Empty PDF URL for ${name}`);
          }
        }
      } catch (err) {
        console.error(`Failed to process ${name}, retrying...`, err);
        try {
          setProgress({ current: i, total, label: `Retrying ${name}...` });
          await new Promise(r => setTimeout(r, 2000));

          await client.mutate<UpdateQuizResponseResponse, UpdateQuizResponseVariables>({
            mutation: UPDATE_QUIZ_RESPONSE_MUTATION,
            variables: {
              id: response.id,
              input: {
                isReportApproved: true,
                aiReportData: null,
              },
            },
            errorPolicy: 'none',
          });

          const pdfResult = await client.query<IndividualPdfResponse, IndividualPdfVars>({
            query: GET_INDIVIDUAL_PDF,
            variables: { responseId: response.id },
            fetchPolicy: 'network-only',
            errorPolicy: 'none',
          });

          const url = pdfResult.data?.getIndividualReportPdf?.reportUrl;
          if (url) {
            const safeName = name.replace(/[^a-zA-Z0-9-_ ]/g, '').trim();
            collectedFiles.push({ url, filename: `${safeName} - ${typeLabel} Report.pdf` });
          } else {
            newWarnings.push(`Empty PDF URL for ${name} after retry`);
          }
        } catch (retryErr) {
          console.error(`Retry also failed for ${name}:`, retryErr);
          newWarnings.push(`Failed to process ${name}`);
        }
      }

      setProgress({ current: i + 1, total, label: '' });
    }

    // Process team report
    if (freshHasTeamReport) {
      try {
        if (freshSurvey.isTeamReportApproved && freshSurvey.reportUrl) {
          setProgress({ current: freshSurvey.responses.length, total, label: 'Team report - already done, skipping' });
          collectedFiles.push({ url: freshSurvey.reportUrl, filename: 'Team Report.pdf' });
        } else {
          if (!freshSurvey.isTeamReportApproved) {
            setProgress({ current: freshSurvey.responses.length, total, label: 'Approving team report...' });
            await client.mutate({
              mutation: UPDATE_QUIZ,
              variables: {
                id: freshSurvey.id,
                isTeamReportApproved: true,
                aiReportData: freshSurvey.aiReportData,
              },
              errorPolicy: 'none',
            });
          }

          if (freshSurvey.reportUrl) {
            collectedFiles.push({ url: freshSurvey.reportUrl, filename: 'Team Report.pdf' });
          } else {
            setProgress({ current: freshSurvey.responses.length, total, label: 'Generating team report PDF...' });
            const teamPdfResult = await client.query<LeaderPdfResponse, LeaderPdfVars>({
              query: GET_LEADER_PDF,
              variables: { quizId: freshSurvey.id },
              fetchPolicy: 'network-only',
              errorPolicy: 'none',
            });

            const url = teamPdfResult.data?.getLeaderReportPdf?.reportUrl;
            if (url) {
              collectedFiles.push({ url, filename: 'Team Report.pdf' });
            } else {
              newWarnings.push('Empty team report PDF URL');
            }
          }
        }
      } catch (err) {
        console.error('Failed to process team report:', err);
        newWarnings.push('Failed to process team report');
      }
      setProgress({ current: total, total, label: '' });
    }

    if (collectedFiles.length === 0) {
      setError('No PDF files were generated. Cannot create ZIP.');
      return;
    }

    // === STEP 2: Download files to server (one by one with progress) ===
    setStep('downloading');
    for (let i = 0; i < collectedFiles.length; i++) {
      const file = collectedFiles[i];
      setProgress({
        current: i,
        total: collectedFiles.length,
        label: `Downloading ${file.filename}...`,
      });

      try {
        await client.mutate({
          mutation: PREPARE_FILE_FOR_ZIP,
          variables: {
            batchId,
            url: file.url,
            filename: file.filename,
          },
          errorPolicy: 'none',
        });
      } catch (err) {
        console.error(`Failed to download ${file.filename}:`, err);
        newWarnings.push(`Failed to download ${file.filename}`);
      }

      setProgress({ current: i + 1, total: collectedFiles.length, label: '' });
    }

    setWarnings(newWarnings);

    // === STEP 3: Pack ZIP ===
    setStep('packing');
    try {
      await client.mutate({
        mutation: PACK_REPORTS_ZIP,
        variables: { batchId },
        errorPolicy: 'none',
      });
    } catch (err) {
      console.error('ZIP packing failed:', err);
      setError('Failed to pack ZIP file. Click retry to try again.');
      return;
    }

    // === STEP 4: Upload ZIP to S3 ===
    setStep('uploading');
    try {
      const uploadResult = await client.mutate({
        mutation: UPLOAD_REPORTS_ZIP,
        variables: {
          batchId,
          quizId: survey.id,
          zipFilename: survey.title || 'reports',
        },
        errorPolicy: 'none',
      });

      const url = uploadResult.data?.uploadReportsZip?.reportUrl;
      if (url) {
        setZipUrl(url);
        setStep('done');
        refetch();
      } else {
        setError('ZIP upload returned no URL.');
      }
    } catch (err) {
      console.error('ZIP upload failed:', err);
      setError('Failed to upload ZIP file. Click retry to try again.');
    }
  }, [survey.id, survey.title, client, refetch]);

  const handleRetry = () => {
    setError(null);
    runProcess();
  };

  return (
    <Modal heading="Download All Reports" onClose={onClose}>
      {step === 'confirm' && (
        <>
          <div className="space-y-3 mb-6">
            <p className="text-sm text-gray-700">
              This will generate AI data, approve all reports, generate PDFs, and package them into a ZIP file.
            </p>
            <div className="text-sm space-y-1">
              <p><strong>{totalResponses}</strong> individual report(s)</p>
              {needsAiData > 0 && (
                <p className="text-amber-600">
                  {needsAiData} report(s) need AI data generated
                </p>
              )}
              {needsApproval > 0 && (
                <p className="text-amber-600">
                  {needsApproval} report(s) will be auto-approved
                </p>
              )}
              {hasTeamReport && (
                <p>
                  + Team report
                  {!survey.isTeamReportApproved && (
                    <span className="text-amber-600"> (will be auto-approved)</span>
                  )}
                </p>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button onClick={onClose}>Cancel</Button>
            <Button
              onClick={runProcess}
              theme="primary"
              disabled={totalResponses === 0 && !hasTeamReport}
            >
              Approve & Download All
            </Button>
          </div>
        </>
      )}

      {step === 'processing' && !error && (
        <div className="py-8 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-4"></div>
          <p className="text-sm text-gray-700 font-semibold mb-1">
            Processing reports: {progress.current} / {progress.total}
          </p>
          {progress.label && (
            <p className="text-xs text-gray-500 mb-3">{progress.label}</p>
          )}
          <div className="w-full bg-gray-200 rounded-full h-2 mt-2 max-w-xs mx-auto">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all"
              style={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
            />
          </div>
        </div>
      )}

      {step === 'downloading' && !error && (
        <div className="py-8 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-4"></div>
          <p className="text-sm text-gray-700 font-semibold mb-1">
            Downloading files: {progress.current} / {progress.total}
          </p>
          {progress.label && (
            <p className="text-xs text-gray-500 mb-3">{progress.label}</p>
          )}
          <div className="w-full bg-gray-200 rounded-full h-2 mt-2 max-w-xs mx-auto">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all"
              style={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
            />
          </div>
        </div>
      )}

      {step === 'packing' && !error && (
        <div className="py-8 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-4"></div>
          <p className="text-sm text-gray-700">Packing ZIP...</p>
        </div>
      )}

      {step === 'uploading' && !error && (
        <div className="py-8 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-4"></div>
          <p className="text-sm text-gray-700">Uploading ZIP to storage...</p>
        </div>
      )}

      {step === 'done' && (
        <div className="py-4">
          <p className="text-sm text-green-700 font-semibold mb-4">ZIP file ready!</p>
          {warnings.length > 0 && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded text-sm">
              <p className="font-semibold text-amber-700 mb-1">Warnings:</p>
              {warnings.map((w, i) => (
                <p key={i} className="text-amber-600">{w}</p>
              ))}
            </div>
          )}
          <div className="flex justify-end gap-3">
            <Button onClick={onClose}>Close</Button>
            <Button theme="primary" onClick={() => window.open(zipUrl!, '_blank')}>
              Download ZIP
            </Button>
          </div>
        </div>
      )}

      {error && (
        <div className="py-4">
          <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700 mb-4">
            {error}
          </div>
          <div className="flex justify-end gap-3">
            <Button onClick={onClose}>Cancel</Button>
            <Button onClick={handleRetry} theme="primary">Retry</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
